import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { ordersApi, tradeMethodsApi } from 'api';
import { Loader } from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { OrderContext } from 'context';
import { DeviceOS, QueryKey } from 'enums';
import { useDeepLink } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { useOrderAuth } from 'pages/Order/hooks';
import { Order, TradeMethod } from 'types';
import { deeplinkUtils, userDeviceUtils } from 'utils';

const useDeeplinkPageTranslation = () =>
  useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.deeplink_page',
  });

export const DeeplinkPage: React.FC = () => {
  const {
    orderId,
    authenticated,
    authenticationFailed,
    setAuthenticationFailed,
    isAuthLoading,
  } = useOrderAuth();

  const orderQuery = useQuery(
    [QueryKey.Order, orderId, 'deeplink-page'],
    () => ordersApi.getOne(orderId),
    {
      enabled: authenticated,
      onError: () => setAuthenticationFailed(true),
    },
  );

  const tradeMethodQuery = useQuery(
    [QueryKey.TradeMethods, orderId, 'selected-deeplink-page'],
    () => tradeMethodsApi.getSelectedTradeMethod(orderId),
    {
      enabled: authenticated,
      onError: () => setAuthenticationFailed(true),
    },
  );

  if (isAuthLoading || orderQuery.isLoading || tradeMethodQuery.isLoading) {
    return <Loader />;
  }

  if (
    authenticationFailed ||
    orderQuery.isError ||
    tradeMethodQuery.isError ||
    !orderQuery.data
  ) {
    return <UnavailableMessage />;
  }

  return (
    <OrderContext.Provider value={{ order: orderQuery.data } as any}>
      <DeeplinkLauncher
        order={orderQuery.data}
        tradeMethod={tradeMethodQuery.data}
      />
    </OrderContext.Provider>
  );
};

const UnavailableMessage: React.FC = () => {
  const { t } = useDeeplinkPageTranslation();
  return <DeeplinkPageMessage title={t('unavailable')} />;
};

export const DeeplinkLauncher: React.FC<{
  order: Order;
  tradeMethod: TradeMethod | undefined;
}> = ({ order, tradeMethod }) => {
  const { t } = useDeeplinkPageTranslation();
  const { processDeeplinkUrl, openDeepLinks } = useDeepLink();
  const [searchParams] = useSearchParams();
  const autoOpenAttemptedRef = useRef(false);
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);
  const isDesktop = deviceOS === DeviceOS.Desktop;
  const customerBank =
    searchParams.get('customerBank') || order.payment?.customerBank;

  const processedUrls = useMemo(
    () =>
      deeplinkUtils
        .getMatchingDeeplinks({
          deeplinks: tradeMethod?.deeplinks,
          customerBank,
          deviceOS,
          requisites: order.requisites,
          // a desktop visitor gets the "open on your phone" hint, which needs
          // the links that exist for any OS to know the hint is worth showing
          includeAllOsOnDesktop: true,
        })
        .map((deeplink) => processDeeplinkUrl(deeplink.url)),
    [
      customerBank,
      deviceOS,
      order.requisites,
      processDeeplinkUrl,
      tradeMethod?.deeplinks,
    ],
  );

  const deeplinkUrl = useMemo(() => processedUrls.join(','), [processedUrls]);

  const open = useCallback(async () => {
    if (!deeplinkUrl) {
      return;
    }
    await openDeepLinks(deeplinkUrl);
  }, [deeplinkUrl, openDeepLinks]);

  useEffect(() => {
    if (isDesktop || !deeplinkUrl || autoOpenAttemptedRef.current) {
      return;
    }
    autoOpenAttemptedRef.current = true;
    void open();
  }, [isDesktop, deeplinkUrl, open]);

  if (isEmpty(processedUrls)) {
    return (
      <DeeplinkPageMessage
        title={t('no_deeplinks')}
        returnUrl={order.integration?.returnUrl}
      />
    );
  }

  if (isDesktop) {
    return (
      <DeeplinkPageMessage
        title={t('desktop_title')}
        description={t('desktop_description')}
        testId="deeplink-desktop-message"
        returnUrl={order.integration?.returnUrl}
      />
    );
  }

  return (
    <div
      data-testid="deeplink-opening-screen"
      className="tw-min-h-[70vh] tw-flex tw-items-center tw-justify-center tw-p-4"
    >
      <div className="tw-w-full tw-max-w-md tw-text-center">
        <h1 className="tw-text-xl tw-font-semibold tw-mb-3">
          {t('opening_title')}
        </h1>
        <p className="tw-text-gray-600 tw-mb-6">{t('opening_description')}</p>
        <button
          type="button"
          onClick={open}
          data-testid="deeplink-open-bank-app-button"
          className="tw-w-full tw-h-[52px] tw-rounded-full tw-bg-blue-600 tw-text-white tw-font-medium"
        >
          {t('open_button')}
        </button>
        <BackToOrderButton returnUrl={order.integration?.returnUrl} />
      </div>
    </div>
  );
};

const DeeplinkPageMessage: React.FC<{
  title: string;
  description?: string;
  testId?: string;
  returnUrl?: string;
}> = ({ title, description, testId, returnUrl }) => {
  const { t } = useDeeplinkPageTranslation();
  return (
    <div
      data-testid={testId}
      className="tw-min-h-[70vh] tw-flex tw-items-center tw-justify-center tw-p-4"
    >
      <div className="tw-w-full tw-max-w-md tw-text-center">
        <h1 className="tw-text-xl tw-font-semibold tw-mb-3">{title}</h1>
        <p className="tw-text-gray-600">
          {description || t('fallback_description')}
        </p>
        <div className="tw-mt-6">
          <BackToOrderButton returnUrl={returnUrl} />
        </div>
      </div>
    </div>
  );
};

const BACK_BUTTON_CLASS_NAME =
  'tw-mt-3 tw-inline-flex tw-w-full tw-h-[52px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-text-gray-800 tw-font-medium';

const BackToOrderButton: React.FC<{ returnUrl?: string }> = ({ returnUrl }) => {
  const { t } = useDeeplinkPageTranslation();
  const { id, token } = useParams<{ id: string; token: string }>();
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;
  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  // the merchant-provided return URL always wins over browser history
  if (returnUrl) {
    return (
      <a
        href={returnUrl}
        data-testid="deeplink-back-to-order-link"
        className={`${BACK_BUTTON_CLASS_NAME} tw-no-underline`}
      >
        {t('back_button')}
      </a>
    );
  }

  // a QR scan opens this page in a fresh tab with no history, so the way back
  // is a direct link to the order built from the same url params
  if (!canGoBack) {
    return (
      <a
        href={ROUTE_PATH.ORDER.replace(':id', id ?? '').replace(
          ':token',
          token ?? '',
        )}
        data-testid="deeplink-back-to-order-link"
        className={`${BACK_BUTTON_CLASS_NAME} tw-no-underline`}
      >
        {t('back_button')}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={goBack}
      data-testid="deeplink-back-to-order-button"
      className={BACK_BUTTON_CLASS_NAME}
    >
      {t('back_button')}
    </button>
  );
};
